import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExercisePicker from './ExercisePicker';
import {
  DEFAULT_REST_SEC, FEELINGS, buildWorkoutExercises, elapsedSince, errorText, formatDate, formatDuration, formatKg, formatSet,
  formatTarget, parseApiDate, prefillFor, setsFor, stepValue, takesLoad, toSetRequest, workingSets
} from './strengthFormat';
import './WorkoutLogger.css';

function Stepper({ label, value, step, onChange, suffix, inputMode = 'decimal' }) {
  return (
    <label className="st-stepper">
      <span className="st-stepper-label">{label}</span>
      <span className="st-stepper-controls">
        <button type="button" onClick={() => onChange(stepValue(value, step, -1))} aria-label={`${label} −`}>−</button>
        <input
          type="number"
          inputMode={inputMode}
          min="0"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" onClick={() => onChange(stepValue(value, step, 1))} aria-label={`${label} +`}>+</button>
      </span>
      {suffix && <span className="st-stepper-suffix">{suffix}</span>}
    </label>
  );
}

function WorkoutLogger({ api, t, lang, session, plan, catalog, catalogByKey, showMessage, onFinished, onDiscarded }) {
  const extrasStorageKey = `kosmoholm-strength-extras-${session.id}`;
  const planDay = plan?.days.find(d => d.id === session.workoutPlanDayId) || null;

  const [sets, setSets] = useState(session.sets || []);
  const [extraKeys, setExtraKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem(extrasStorageKey) || '[]'); } catch { return []; }
  });
  const [guidance, setGuidance] = useState({});
  const [edits, setEdits] = useState({});
  const [rest, setRest] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [feeling, setFeeling] = useState(null);
  const [busy, setBusy] = useState(null);
  const [briefing, setBriefing] = useState(null);
  // Per-exercise comments: saved drafts keyed by exercise, and which note boxes are open.
  const [noteDrafts, setNoteDrafts] = useState(() =>
    Object.fromEntries((session.exerciseNotes || []).filter(n => n.note).map(n => [n.exerciseKey, n.note])));
  const [savedNotes, setSavedNotes] = useState(noteDrafts);
  const [openNotes, setOpenNotes] = useState({});
  const noteSaves = useRef({});

  // What the coach asked to focus on after the last relevant workout.
  useEffect(() => {
    let cancelled = false;
    api.briefing(session.workoutPlanDayId)
      .then(b => { if (!cancelled) setBriefing(b); })
      .catch(e => console.error('Failed to load briefing:', e));
    return () => { cancelled = true; };
  }, [api, session.workoutPlanDayId]);

  const saveNote = (key) => {
    const text = (noteDrafts[key] || '').trim();
    const pending = noteSaves.current[key];
    // Leaving the box and tapping Finish both save: reuse the save already on its way.
    if (pending && pending.text === text) return pending.promise;
    if (!pending && text === (savedNotes[key] || '')) return Promise.resolve();

    const promise = (pending ? pending.promise : Promise.resolve())
      .then(() => api.setExerciseNote(session.id, key, text))
      .then(() => setSavedNotes(prev => ({ ...prev, [key]: text })))
      .catch(e => showMessage(errorText(e, t('strength.errors.save')), 'error'))
      .finally(() => {
        if (noteSaves.current[key]?.promise === promise) delete noteSaves.current[key];
      });
    noteSaves.current[key] = { text, promise };
    return promise;
  };

  const exercises = useMemo(
    () => buildWorkoutExercises(planDay, sets, extraKeys, catalogByKey),
    [planDay, sets, extraKeys, catalogByKey]
  );
  const exerciseKeys = exercises.map(e => e.key).join(',');

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (rest && now >= rest.endsAt) {
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      setRest(null);
    }
  }, [now, rest]);

  useEffect(() => {
    localStorage.setItem(extrasStorageKey, JSON.stringify(extraKeys));
  }, [extraKeys, extrasStorageKey]);

  // Last time and today's suggestion for every exercise on screen; fetched once per exercise.
  useEffect(() => {
    const missing = exerciseKeys.split(',').filter(k => k && !(k in guidance));
    if (missing.length === 0) return;
    let cancelled = false;
    api.guidance(missing, session.workoutPlanDayId)
      .then(list => {
        if (cancelled) return;
        setGuidance(prev => {
          const next = { ...prev };
          missing.forEach(k => { next[k] = null; });
          (list || []).forEach(g => { next[g.exerciseKey] = g; });
          return next;
        });
      })
      .catch(e => console.error('Failed to load guidance:', e));
    return () => { cancelled = true; };
  }, [api, exerciseKeys, guidance, session.workoutPlanDayId]);

  const valuesFor = (exercise) =>
    edits[exercise.key] || { ...prefillFor(exercise, guidance[exercise.key], setsFor(sets, exercise.key)), isWarmup: false };

  const edit = (exercise, field, value) =>
    setEdits(prev => ({ ...prev, [exercise.key]: { ...valuesFor(exercise), [field]: value } }));

  const logSet = async (exercise) => {
    const values = valuesFor(exercise);
    const request = toSetRequest(exercise, values, values.isWarmup);
    if (!request) {
      showMessage(t(exercise.kind === 'Timed' ? 'strength.logger.enterDuration' : 'strength.logger.enterReps'), 'error');
      return;
    }
    setBusy(exercise.key);
    try {
      const created = await api.logSet(session.id, request);
      setSets(prev => [...prev, created]);
      setEdits(prev => {
        const next = { ...prev };
        delete next[exercise.key];
        return next;
      });
      if (!request.isWarmup) {
        const restSec = exercise.target?.restSec ?? DEFAULT_REST_SEC;
        if (restSec > 0) setRest({ endsAt: Date.now() + restSec * 1000, total: restSec, name: exercise.name });
      }
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const deleteSet = async (set) => {
    try {
      await api.deleteSet(session.id, set.id);
      setSets(prev => prev
        .filter(s => s.id !== set.id)
        .map(s => (s.exerciseKey === set.exerciseKey && s.setNumber > set.setNumber ? { ...s, setNumber: s.setNumber - 1 } : s)));
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    }
  };

  const finish = async () => {
    setBusy('finish');
    try {
      // Save any note still being typed, so the coach's review reads it.
      await Promise.all(Object.keys(noteDrafts).map(saveNote));
      const result = await api.finishSession(session.id, notes, feeling);
      localStorage.removeItem(extrasStorageKey);
      onFinished(result);
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
      setBusy(null);
    }
  };

  const discard = async () => {
    if (!window.confirm(t('strength.logger.discardConfirm'))) return;
    try {
      await api.deleteSession(session.id);
      localStorage.removeItem(extrasStorageKey);
      onDiscarded();
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    }
  };

  const workingCount = workingSets(sets).length;
  const restLeft = rest ? Math.max(0, Math.ceil((rest.endsAt - now) / 1000)) : 0;

  return (
    <div className="st-logger">
      <header className="st-card st-logger-head">
        <div>
          <span className="st-eyebrow">{t('strength.logger.inProgress')}</span>
          <h2>{session.name}</h2>
        </div>
        <div className="st-logger-stats">
          <span className="st-clock" aria-label={t('strength.logger.elapsed')}>⏱ {elapsedSince(session.startedAt, now)}</span>
          <span className="st-muted">{t('strength.logger.setsDone', { count: workingCount })}</span>
        </div>
      </header>

      {briefing?.focus?.length > 0 && (
        <section className="st-card st-briefing">
          <h3>🎯 {t('strength.logger.todaysFocus')}</h3>
          <ul>
            {briefing.focus.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="st-muted st-small">
            {t('strength.logger.focusFrom', { name: briefing.sessionName, date: formatDate(parseApiDate(briefing.date), lang) })}
          </p>
        </section>
      )}

      {exercises.length === 0 && (
        <div className="st-empty">{t('strength.logger.noExercises')}</div>
      )}

      {exercises.map(exercise => {
        const logged = setsFor(sets, exercise.key);
        const working = workingSets(logged);
        const targetSets = exercise.target?.targetSets;
        const done = targetSets && working.length >= targetSets;
        const g = guidance[exercise.key];
        const values = valuesFor(exercise);
        const info = catalogByKey[exercise.key];
        const showWeight = takesLoad(info || exercise);
        let workingNumber = 0;

        return (
          <section key={exercise.key} className={`st-card st-exercise ${done ? 'done' : ''}`}>
            <div className="st-exercise-head">
              <h3>{done && <span className="st-check" aria-hidden="true">✓ </span>}{exercise.name}</h3>
              {exercise.target && <span className="st-target">{formatTarget(exercise.target, lang)}</span>}
            </div>

            {g?.lastSets?.length > 0 && (
              <p className="st-last">
                {t('strength.logger.lastTime')}: {workingSets(g.lastSets).map(s => formatSet(s, lang)).join(', ')}
              </p>
            )}
            {g && g.advice !== 'First' && (
              <p className={`st-advice st-advice-${g.advice}`}>
                {t(`strength.advice.${g.advice}`)}
                {g.nextWeightKg ? ` · ${formatKg(g.nextWeightKg, lang)}` : ''}
                {g.nextReps ? ` × ${g.nextReps}` : ''}
                {g.nextDurationSec ? ` · ${formatDuration(g.nextDurationSec)}` : ''}
              </p>
            )}
            {g?.coachCue && <p className="st-coach-cue">🎯 {t('strength.logger.coachSays')}: {g.coachCue}</p>}
            {g?.lastNote && !(g.coachCue || '').includes(g.lastNote) && (
              <p className="st-last-note">📝 {t('strength.logger.lastNote')}: «{g.lastNote}»</p>
            )}
            {exercise.target?.notes && <p className="st-cue">💡 {exercise.target.notes}</p>}

            {logged.length > 0 && (
              <ol className="st-set-list">
                {logged.map(set => {
                  if (!set.isWarmup) workingNumber += 1;
                  return (
                    <li key={set.id} className={set.isWarmup ? 'warmup' : ''}>
                      <span className="st-set-no">{set.isWarmup ? 'W' : workingNumber}</span>
                      <span className="st-set-value">{formatSet(set, lang)}</span>
                      <button
                        type="button"
                        className="st-icon-btn"
                        onClick={() => deleteSet(set)}
                        aria-label={t('strength.logger.deleteSet')}
                      >✕</button>
                    </li>
                  );
                })}
              </ol>
            )}

            <div className="st-input-row">
              {showWeight && (
                <Stepper
                  label={t('strength.logger.weight')}
                  value={values.weightKg}
                  step={info?.weightIncrementKg || 2.5}
                  suffix="kg"
                  onChange={(v) => edit(exercise, 'weightKg', v)}
                />
              )}
              {exercise.kind === 'Timed' ? (
                <Stepper
                  label={t('strength.logger.seconds')}
                  value={values.durationSec}
                  step={5}
                  suffix="s"
                  inputMode="numeric"
                  onChange={(v) => edit(exercise, 'durationSec', v)}
                />
              ) : (
                <Stepper
                  label={t('strength.logger.reps')}
                  value={values.reps}
                  step={1}
                  inputMode="numeric"
                  onChange={(v) => edit(exercise, 'reps', v)}
                />
              )}
              <label className="st-warmup-toggle">
                <input
                  type="checkbox"
                  checked={!!values.isWarmup}
                  onChange={(e) => edit(exercise, 'isWarmup', e.target.checked)}
                />
                {t('strength.logger.warmup')}
              </label>
              <button
                type="button"
                className="st-btn st-btn-primary st-log-btn"
                disabled={busy === exercise.key}
                onClick={() => logSet(exercise)}
              >
                ✓ {t('strength.logger.logSet')}
              </button>
            </div>

            {openNotes[exercise.key] ? (
              <label className="st-note-field">
                <span className="st-sr-only">{t('strength.logger.noteLabel', { name: exercise.name })}</span>
                <textarea
                  rows={2}
                  maxLength={1000}
                  autoFocus
                  value={noteDrafts[exercise.key] || ''}
                  placeholder={t('strength.logger.notePlaceholder')}
                  onChange={(e) => setNoteDrafts(prev => ({ ...prev, [exercise.key]: e.target.value }))}
                  onBlur={() => { saveNote(exercise.key); setOpenNotes(prev => ({ ...prev, [exercise.key]: false })); }}
                />
              </label>
            ) : (
              <button
                type="button"
                className="st-note-toggle"
                onClick={() => setOpenNotes(prev => ({ ...prev, [exercise.key]: true }))}
              >
                📝 {savedNotes[exercise.key] ? `«${savedNotes[exercise.key]}»` : t('strength.logger.addNote')}
              </button>
            )}
          </section>
        );
      })}

      <button type="button" className="st-btn st-btn-ghost st-add-exercise" onClick={() => setPickerOpen(true)}>
        ＋ {t('strength.logger.addExercise')}
      </button>

      <section className="st-card st-finish">
        <fieldset className="st-feeling">
          <legend>{t('strength.logger.feeling')}</legend>
          <div className="st-feeling-options">
            {FEELINGS.map(f => (
              <button
                key={f.value}
                type="button"
                className={`st-feeling-option ${feeling === f.value ? 'active' : ''}`}
                aria-pressed={feeling === f.value}
                onClick={() => setFeeling(prev => (prev === f.value ? null : f.value))}
              >
                <span aria-hidden="true">{f.emoji}</span>
                <span>{t(`strength.feelings.${f.value}`)}</span>
              </button>
            ))}
          </div>
        </fieldset>
        <label className="st-field">
          <span>{t('strength.logger.notes')}</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('strength.logger.notesPlaceholder')} />
        </label>
        <div className="st-row st-row-between">
          <button type="button" className="st-btn st-btn-danger-ghost" onClick={discard}>{t('strength.logger.discard')}</button>
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={sets.length === 0 || busy === 'finish'}
            onClick={finish}
          >
            🏁 {t('strength.logger.finish')}
          </button>
        </div>
      </section>

      {rest && (
        <div className="st-rest-bar" role="timer" aria-live="off">
          <div className="st-rest-progress" style={{ width: `${(restLeft / rest.total) * 100}%` }} />
          <span className="st-rest-label">{t('strength.logger.rest')} · {formatDuration(restLeft)}</span>
          <span className="st-rest-actions">
            <button type="button" onClick={() => setRest(r => ({ ...r, endsAt: r.endsAt + 30000, total: r.total + 30 }))}>+30 s</button>
            <button type="button" onClick={() => setRest(null)}>{t('strength.logger.skipRest')}</button>
          </span>
        </div>
      )}

      {pickerOpen && (
        <ExercisePicker
          t={t}
          catalog={catalog}
          excludeKeys={exercises.map(e => e.key)}
          onPick={(key) => {
            setExtraKeys(prev => (prev.includes(key) ? prev : [...prev, key]));
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default WorkoutLogger;
